import React from 'react';
import { motion } from 'framer-motion';
import { Settings, Clock, Sun, Moon, Zap, CheckCircle2, X } from 'lucide-react';

const SettingsSection = ({ 
  church, 
  setChurch, 
  saveStatus, 
  onSaveSettings 
}) => {
  return (
    <div className="space-y-6" data-testid="settings-section">
      {/* Settings Header */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Church Settings</h2>
            <p className="text-sm text-slate-500">Configure your church's basic information</p>
          </div>
        </div>
        
        <div className="grid gap-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Church Name</label>
              <input
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                value={church.name}
                onChange={e => setChurch(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter church name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Church Address</label>
              <input
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
                value={church.address}
                onChange={e => setChurch(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter full address"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Verse of the Day</label>
            <textarea
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none resize-none"
              rows={3}
              value={church.verseOfDay}
              onChange={e => setChurch(prev => ({ ...prev, verseOfDay: e.target.value }))}
              placeholder="Enter scripture verse"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Online Giving URL</label>
            <input
              type="url"
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
              value={church.givingUrl}
              onChange={e => setChurch(prev => ({ ...prev, givingUrl: e.target.value }))}
              placeholder="https://your-giving-platform.com"
            />
          </div>
          
          <motion.button 
            onClick={onSaveSettings}
            className={`w-full py-4 rounded-xl font-bold text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 ${
              saveStatus === "saving" ? "bg-slate-400" :
              saveStatus === "saved" ? "bg-emerald-500" :
              saveStatus === "error" ? "bg-red-500" :
              "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            }`}
            whileHover={{ scale: saveStatus ? 1 : 1.01 }}
            whileTap={{ scale: saveStatus ? 1 : 0.99 }}
            disabled={saveStatus === "saving"}
          >
            {saveStatus === "saving" ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                >
                  <Settings className="w-5 h-5" />
                </motion.div>
                <span>Saving...</span>
              </>
            ) : saveStatus === "saved" ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Settings Saved!</span>
              </>
            ) : saveStatus === "error" ? (
              <>
                <X className="w-5 h-5" />
                <span>Error Saving</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Save Settings</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
      
      {/* Service Times Card */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Service Times</h2>
            <p className="text-sm text-slate-500">Configure weekly service schedules</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: "sunday", label: "Sunday Service", icon: Sun },
            { key: "wedMorning", label: "Wed Morning", icon: Clock },
            { key: "wedEvening", label: "Wed Evening", icon: Moon },
            { key: "fridayEvening", label: "Friday Evening", icon: Zap }
          ].map((service) => (
            <div key={service.key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <service.icon className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">{service.label}</span>
              </div>
              <input
                type="time"
                className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:border-blue-400 transition-colors text-slate-800"
                value={church.serviceTimes?.[service.key] || ""}
                onChange={e => setChurch(prev => ({
                  ...prev,
                  serviceTimes: { ...prev.serviceTimes, [service.key]: e.target.value }
                }))}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsSection;
